// Salt-mixing math for the water-change sheet. Pure functions over the
// admin-editable salt_mixes config blob (see DEFAULT_SALT_MIXES in
// server/db/seeds). Amounts scale linearly with the tank's salinity
// target — brand tables are published for 35 ppt.

import { sgToPpt } from './salinity.js';

const L_PER_GAL = 3.785411784;

// Grams of dry mix (and cups, when the brand publishes a scoop
// guideline) to bring `gallons` of RO/DI to `targetPpt`.
export function saltForChange({ gallons, gPerL, targetPpt = 35, cupsPerGal = null }) {
  const gal = Number(gallons);
  const rate = Number(gPerL);
  // Detection-first, mirroring salinity.js: a legacy target stored on
  // the S.G. scale (1.026) passes a bare > 0 check and would scale the
  // salt amount by 1.026/35 — a ~35x underdose the keeper would trust
  // (review finding). S.G. lives below 2; convert instead of obeying.
  const rawTarget = Number(targetPpt);
  const target =
    rawTarget > 0.9 && rawTarget < 2 ? sgToPpt(rawTarget) : rawTarget > 2 ? rawTarget : 35;
  if (!Number.isFinite(gal) || gal <= 0 || !Number.isFinite(rate) || rate <= 0) return null;
  const scale = target / 35;
  const grams = gal * L_PER_GAL * rate * scale;
  const cups =
    Number.isFinite(Number(cupsPerGal)) && Number(cupsPerGal) > 0
      ? gal * Number(cupsPerGal) * scale
      : null;
  return {
    grams: Math.round(grams),
    cups: cups != null ? Math.round(cups * 10) / 10 : null,
  };
}

// Best-effort match of a tracked supply ("Red Sea Coral Pro 22 kg
// bucket") to a brand row in the config. Longest label-word overlap
// wins; ties break toward the more specific (longer) label so
// "Coral Pro" beats plain "Red Sea". Returns the brand or null.
export function matchBrand(brands, supplyName) {
  const name = String(supplyName || '').toLowerCase();
  if (!name || !Array.isArray(brands)) return null;
  let best = null;
  let bestScore = 0;
  for (const b of brands) {
    if (b?.key === 'other') continue;
    const words = String(b?.label || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((w) => w.length > 2);
    if (!words.length) continue;
    const hits = words.filter((w) => name.includes(w)).length;
    if (!hits) continue;
    const score = hits + words.length / 100;
    if (score > bestScore) {
      bestScore = score;
      best = b;
    }
  }
  return best;
}

// A salt box's yield in the tank's volume unit. Packages are listed by the
// gallons they mix; a litres keeper's box was saved and shown as "160 gal".
// The water-change draw-down reads the supply's own unit (gal or L), so a
// box kept in litres stays right.
export function saltSupplyYield(gallons, volumeUnit) {
  const g = Number(gallons);
  if (!Number.isFinite(g) || g <= 0) return null;
  if (volumeUnit !== 'L') return { unit: 'gal', size: g };
  return { unit: 'L', size: Math.round(g * L_PER_GAL * 10) / 10 };
}
